const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
const norm = value => clean(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const instances = new Set();
const feedCache = new Map();
const readJSON = (root, selector) => {
  try { return JSON.parse(root.querySelector(selector)?.textContent || '{}'); } catch { return {}; }
};
const safeURL = (value, local = false) => {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    if (!['https:', 'http:'].includes(url.protocol) || (local && url.origin !== window.location.origin)) return '';
    return url.href;
  } catch { return ''; }
};
function descriptionLines(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('table,script,style,iframe,svg,noscript').forEach(node => node.remove());
  doc.querySelectorAll('br').forEach(node => node.replaceWith('\n'));
  doc.querySelectorAll('p,li,div,h1,h2,h3,h4').forEach(node => node.append('\n'));
  return (doc.body.textContent || '').split(/\n/).map(clean).filter(Boolean);
}
function classify(product) {
  const classifyText = text => {
    if(/\b(sunglasses|eyewear|sun glasses|occhiali)\b/.test(text)) return 'sunglasses';
    if(/\b(sneakers?|shoes?|boots?|loafers?|mules?|sandals?|clogs?|slippers?)\b/.test(text)) return 'shoes';
    if(/\b(dress(?:es)?|jumpsuits?|rompers?|dungarees|overalls)\b/.test(text)) return 'dress';
    if(/\b(tracksuits?|co[ -]?ords?|matching sets?|two[ -]piece sets?)\b/.test(text)) return 'set';
    if(/\b(bags?|backpacks?|totes?|caps?|hats?|beanies|belts?|scarves|socks?)\b/.test(text)) return 'accessory';
    if (/\b(jacket|coat|parka|overshirt|blazer|windbreaker|puffer|anorak)\b/.test(text)) return 'outer';
    if (/\b(jeans?|pants?|trousers?|sweatpants|joggers?|shorts?|chinos?|leggings|skirt)\b/.test(text)) return 'bottom';
    if (/\b(hoodie|hoodies|sweatshirt|sweater|cardigan|fleece|pullover|vest)\b/.test(text)) return 'mid';
    if (/\b(t[ -]?shirts?|tees?|henley|polo|shirts?|tops?|tank|camisole)\b/.test(text)) return 'base';
    return '';
  };
  return classifyText(norm(product.title)) || classifyText(norm(product.type)) || classifyText(norm((product.tags || []).join(' ')));
}
function analyse(product) {
  const lines = descriptionLines(product.description);
  const fields = new Map();
  const fieldPattern = /^(material(?:\s*\([^)]*\))?|composition|fabric(?:\s+weight)?|lining|shell|fit|details|thickness|weight)\s*[:：]\s*(.+)$/i;
  for (const line of lines) {
    const match = line.match(fieldPattern);
    if (match && match[2].length <= 280) fields.set(norm(match[1]), clean(match[2]));
  }
  const text = norm(product.title + ' ' + lines.join(' '));
  const facts = [];
  const add = (key, value) => {
    if (value && !facts.some(fact => fact.key === key || norm(fact.value) === norm(value))) facts.push({ key, value: clean(value) });
  };
  const shell = fields.get('material (shell)') || fields.get('shell');
  const lining = fields.get('material (inside)') || fields.get('lining') || fields.get('material (lining)');
  if (shell) add('shell', shell);
  else add('material', fields.get('material') || fields.get('composition') || fields.get('fabric'));
  add('lining', lining);
  const weight = fields.get('fabric weight') || fields.get('weight') || (product.title + ' ' + lines.join(' ')).match(/\b(?:approx\.?\s*)?\d+(?:\.\d+)?\s*(?:gsm|g\/m[²2]|oz)\b/i)?.[0] || product.title?.match(/\b\d+(?:\.\d+)?\s*g\b/i)?.[0];
  add('weight', weight);
  const featureRules = [
    ['construction', /\b(?:double[ -]layer(?:ed)?)(?:\s+construction)?\b/i],
    ['texture', /\bwaffle(?:[ -](?:textured|knit))?(?:\s+(?:texture|fabric|knit))?\b/i],
    ['texture', /\bcable[ -]knit(?:ted)?(?:\s+(?:construction|pattern))?\b/i],
    ['texture', /\b(?:ribbed|jacquard|fine[ -]knit|pique|piqué|corduroy|canvas|denim)(?:\s+(?:knit|fabric|construction))?\b/i],
    ['neckline', /\b(?:faux[ -]layered neckline|layered[ -]look neckline|contrast (?:white )?inner collar|mock[ -]neck|high collar|crew[ -]neck|v[ -]neck)\b/i],
    ['construction', /\b(?:four|five|three|two|seven|\d+)[ -]button\s+(?:placket|front)\b/i],
    ['finish', /\b(?:garment[ -]washed(?: finish)?|garment[ -]dyed|distressed hems?|raw hems?|washed finish|ribbed cuffs(?: and hem)?)\b/i]
  ];
  const source = product.title + ' ' + lines.join(' ');
  for (const [key, pattern] of featureRules) add(key, source.match(pattern)?.[0]);
  const explicitFit = fields.get('fit');
  const fitSource = explicitFit || source;
  const shapeRules = [
    [/\bbarrel\b/i, 'Barrel'], [/\bbootcut\b/i, 'Bootcut'], [/\bflar(?:e|ed)\b/i, 'Flared'],
    [/\bstraight[ -]leg\b/i, 'Straight leg'], [/\btapered\b/i, 'Tapered'], [/\bwide[ -]leg\b/i, 'Wide leg'],
    [/\bboxy\b/i, 'Boxy'], [/\boversized?\b/i, 'Oversized'], [/\brelaxed\b/i, 'Relaxed'],
    [/\b(?:loose|baggy)\b/i, 'Loose'], [/\bslim(?:[ -]fit)?\b/i, 'Slim'], [/\bfitted\b/i, 'Fitted'],
    [explicitFit ? /\bregular(?:[ -]fit)?\b/i : /\bregular[ -]fit\b/i, 'Regular']
  ];
  const shape = shapeRules.find(([pattern]) => pattern.test(fitSource))?.[1] || '';
  const fitFacts = [];
  const fitAdd = (key, value) => { if (value && !fitFacts.some(item => item.key === key)) fitFacts.push({ key, value: clean(value) }); };
  fitAdd('fit', explicitFit || shape);
  fitAdd('shoulders', source.match(/\b(?:dropped shoulders|drop[ -]shoulder(?:s)?|set[ -]in shoulders|raglan sleeves)\b/i)?.[0]);
  fitAdd('length', source.match(/\b(?:(?:slightly|subtly)\s+)?cropped(?:\s+length)?\b/i)?.[0]);
  fitAdd('waist', source.match(/\b(?:mid|high|low)[ -]rise\b/i)?.[0]);
  if (!explicitFit && classify(product) === 'bottom' && shape) fitFacts[0].key = 'leg';
  if (fitFacts.length < 3) fitAdd('sleeve', source.match(/\b(?:wide|short|long)[ -]sleeves?\b/i)?.[0]);
  let material = norm(shell || fields.get('material') || fields.get('composition') || '');
  material = ['cotton','linen','hemp','wool','silk','modal','lyocell','nylon','polyester'].filter(word => material.includes(word));
  const tagWords = norm((product.tags || []).join(' '));
  const gender = /\bunisex\b/.test(text + ' ' + tagWords) ? 'all' : /\bwomen(?:s)?\b/.test(tagWords) && !/\bmen(?:s)?\b/.test(tagWords) ? 'women' : /\bmen(?:s)?\b/.test(tagWords) && !/\bwomen(?:s)?\b/.test(tagWords) ? 'men' : 'all';
  return { role: classify(product), text, facts: facts.slice(0, 3), fitFacts: fitFacts.slice(0, 3), shape, material, gender,
    warm: /\b(heavyweight|sherpa|fleece|padded|down|wool|double[ -]layer)\b/.test(text),
    light: /\b(shorts|linen|hemp|lightweight|tank|short[ -]sleeve)\b/.test(text),
    cropped: /\bcropped\b/.test(text), denim: /\bdenim|jeans\b/.test(text)
  };
}
function colorFamily(value) {
  const text = norm(value);
  const rules = [
    ['multi', /multi|stripe|colorblock|colourblock|camo|leopard|plaid/],
    ['black', /black|nero|noir/], ['navy', /navy|midnight/], ['cream', /cream|ivory|ecru|off[ -]white|apricot/],
    ['white', /white|bianco|blanc/], ['gray', /gr[ae]y|ash|charcoal|silver/],
    ['brown', /brown|chocolate|coffee|mocha|cocoa/], ['beige', /beige|sand|khaki|oat|taupe|camel/],
    ['green', /green|olive|sage|moss/], ['blue', /blue|indigo|denim|azure/], ['red', /red|burgundy|wine|maroon/],
    ['purple', /purple|violet|lavender/], ['pink', /pink|rose|blush/], ['yellow', /yellow|mustard/], ['orange', /orange|rust/]
  ];
  return rules.find(([, pattern]) => pattern.test(text))?.[0] || '';
}
function colorScore(source, candidate) {
  const a = colorFamily(source), b = colorFamily(candidate);
  if (!b || !a) return 0;
  if (a === b) return 5;
  const neutrals = ['black','navy','white','cream','gray','brown','beige'];
  if (neutrals.includes(a) && neutrals.includes(b)) return 7;
  if (neutrals.includes(b)) return 6;
  if ((['blue','green'].includes(a) && ['brown','beige','cream'].includes(b)) || (a === 'brown' && b === 'blue')) return 7;
  if (a === 'multi' || b === 'multi') return -3;
  return -2;
}
const productColor = product => product.colorIndex >= 0 ? product.selected?.options?.[product.colorIndex] || '' : '';
function scoreCandidate(current, candidate, sourceColor, round = 0) {
  if (!candidate.available || !candidate.image || !safeURL(candidate.url,true) || String(candidate.id) === String(current.id) || !candidate.colors?.length) return null;
  const a = current.analysis || analyse(current), b = candidate.analysis || analyse(candidate);
  if (!b.role || a.role === b.role || (a.gender !== 'all' && b.gender !== 'all' && a.gender !== b.gender)) return null;
  const colors = candidate.colors.filter(color => safeURL(color.image));
  if (!colors.length) return null;
  const sortedColors = colors.map(color => ({ color, score: colorScore(sourceColor, color.color) })).sort((x,y) => y.score - x.score);
  const selected = sortedColors[0];
  let score = 20 + selected.score;
  if (a.warm && b.warm) score += 2;
  if (a.light && b.light) score += 3;
  if (a.light && b.warm) score -= 8;
  if (a.material.some(material => b.material.includes(material))) score += 2;
  if (a.cropped && /wide|straight|loose|barrel/i.test(b.shape)) score += 3;
  if (a.denim && /waffle|henley|knit/.test(b.text)) score += 2;
  const seed = ((Number(current.id) % 997) * 31 + Number(candidate.id) % 997) % 101;
  score += seed / 101;
  return { product: candidate, color: selected.color, role: b.role, score };
}
const productName = choice => clean(choice.product.title+(choice.color.color?' — '+choice.color.color:''));
function outfitSlots(product) {
  const role=(product.analysis || analyse(product)).role;
  if(role==='sunglasses') return ['base','bottom','shoes'];
  if(role==='shoes' || role==='accessory') return ['base','bottom','sunglasses'];
  if(role==='dress' || role==='set') return ['shoes','outer','sunglasses'];
  const slots=role==='base'?['bottom','layer']:role==='mid'?['bottom','base']:role==='outer'?['base','bottom']:role==='bottom'?['base','layer']:[];
  return slots.length ? [...slots,'sunglasses'] : [];
}
const matchesSlot = (role, slot) => slot==='layer' ? ['outer','mid'].includes(role) : role===slot;
const choiceKey = choice => choice ? `${choice.product.id}:${choice.color.id}` : '';
function chooseOutfit(current, candidates, round=0, options={}) {
  const slots=options.slots || outfitSlots(current), locks=options.locks || new Map();
  const previous=options.previous || [], target=options.target || null;
  const retained=new Map(locks);
  if(target) for(const choice of previous) if(choice.slot!==target && slots.includes(choice.slot)) retained.set(choice.slot,choice);
  const seen=new Set([String(current.id)]);
  for(const slot of slots) if(retained.has(slot)) seen.add(String(retained.get(slot).product.id));
  const scored=candidates.map(candidate=>scoreCandidate(current,candidate,productColor(current),round)).filter(Boolean);
  const choices=[];
  for(const slot of slots) {
    if(retained.has(slot)) { choices.push({...retained.get(slot),slot});continue; }
    const eligible=scored.filter(item=>!seen.has(String(item.product.id)) && matchesSlot(item.role,slot)).sort((a,b)=>b.score-a.score);
    if(!eligible.length) continue;
    const best=eligible.filter(item=>item.score>=eligible[0].score-8).slice(0,8);
    const prior=previous.find(item=>item.slot===slot);
    const index=best.findIndex(item=>String(item.product.id)===String(prior?.product.id));
    const chosen=best[index>=0 ? (index+1)%best.length : round%best.length];
    choices.push({...chosen,slot});seen.add(String(chosen.product.id));
  }
  return choices;
}
function resizeURL(value, width) {
  const valid = safeURL(value);
  if (!valid) return '';
  const url = new URL(valid); url.searchParams.set('width', width); return url.href;
}
function imageElement(src, alt, className = '') {
  const image = document.createElement('img');
  image.src = resizeURL(src, 720); image.alt = alt; image.className = className;
  image.width = 720; image.height = 900; image.loading = 'lazy'; image.decoding = 'async';
  image.srcset = [360,540,720,1000].map(width => `${resizeURL(src,width)} ${width}w`).join(', ');
  image.sizes = '(min-width: 1000px) 24vw, (min-width: 700px) 22vw, 46vw';
  return image;
}
async function getFeed(url, page = 1, force = false) {
  const valid = safeURL(url, true);
  if (!valid) throw new Error('Invalid feed URL');
  const endpoint = new URL(valid);
  endpoint.searchParams.set('section_id','cz-uniform-feed');
  if (page > 1) endpoint.searchParams.set('page',String(page));
  if (endpoint.pathname.includes('/collections/')) endpoint.searchParams.set('sort_by','best-selling');
  const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
  if (preview) endpoint.searchParams.set('preview_theme_id',preview);
  const key = endpoint.href;
  if (force) feedCache.delete(key);
  if (!feedCache.has(key)) {
    const promise = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(),10000);
      try {
        const response = await fetch(key,{ credentials:'same-origin', signal:controller.signal, headers:{Accept:'text/html'} });
        if (!response.ok) throw new Error('Feed unavailable');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const payload = doc.querySelector('[data-czu-feed]');
        if (!payload) throw new Error('Feed section missing');
        return JSON.parse(payload.textContent);
      } finally { clearTimeout(timer); }
    })();
    feedCache.set(key,promise);
    promise.catch(() => feedCache.delete(key));
  }
  return feedCache.get(key);
}
class CZUniform extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => { if (this.isConnected && !this.ready) this.setup(); });
  }
  disconnectedCallback() {
    this.abort?.abort(); this.observer?.disconnect(); this.ready=false; instances.delete(this);
    this.generation=(this.generation || 0)+1;
  }
  setup() {
    this.product=readJSON(this,'[data-czu-product]'); this.labels=readJSON(this,'[data-czu-strings]');
    if (!this.product.id) return;
    this.ready=true; this.abort=new AbortController(); this.round=0; this.generation=0;
    this.locks=new Map();this.choices=[];
    this.product.analysis=analyse(this.product); this.seedColor=productColor(this.product); instances.add(this);
    this.updateVisual();
    this.setupUniform();
  }
  label(key) { return this.labels[key] || key; }
  updateVisual() {
    const color=productColor(this.product);
    const editorial=color===this.seedColor ? (this.dataset.mode==='details'?this.product.detailImage:this.dataset.mode==='fit'?this.product.fitImage:null) : null;
    const src=editorial || this.product.selected?.image || this.product.image;
    this.querySelectorAll('[data-czu-current-color]').forEach(node => {node.textContent=color; node.hidden=!color;});
    this.querySelectorAll('[data-czu-current-price]').forEach(node => {if(this.product.selected?.price) node.textContent=this.product.selected.price;});
    this.querySelectorAll('[data-czu-current-image] img').forEach(image => {
      if(!safeURL(src)) return;
      image.src=resizeURL(src,1000); image.srcset=[360,540,720,960,1200].map(width => `${resizeURL(src,width)} ${width}w`).join(', ');
      image.alt=clean(`${this.product.title}${color?' — '+color:''}`);
    });
  }
  setVariant(variant) {
    if(!variant) return;
    const previous=this.product.selected || {};
    const options=variant.options || [variant.option1,variant.option2,variant.option3].filter(value => value!=null);
    const image=variant.featured_media?.preview_image?.src || variant.featured_image?.src || variant.image;
    this.product.selected={...previous,id:variant.id || previous.id,available:variant.available,options:options.length?options:previous.options,image:image || this.product.image};
    if(typeof variant.price==='string' && /[^\d.]/.test(variant.price)) this.product.selected.price=variant.price;
    else if(typeof variant.price==='number') {
      try { this.product.selected.price=new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:window.Shopify?.currency?.active || 'EUR'}).format(variant.price/100); } catch { this.product.selected.price=previous.price; }
    }
    this.updateVisual(); this.round=0;
    if(this.candidates) this.renderOutfit(false);
  }
  setupUniform() {
    this.pools=readJSON(this,'[data-czu-pools]');
    Object.assign(this.labels,readJSON(this,'[data-czu-lock-strings]'));
    this.addEventListener('click',event=>{
      const change=event.target.closest?.('[data-czu-change]');
      if(change && this.contains(change)) {
        const slot=change.dataset.czuChange;
        if(change.disabled || this.locks.has(slot)) return;
        this.round++;
        this.renderOutfit(true,slot);
        return;
      }
      const button=event.target.closest?.('[data-czu-lock]');
      if(!button || !this.contains(button)) return;
      const slot=button.dataset.czuLock, choice=this.choices.find(item=>item.slot===slot);
      if(!choice) return;
      if(this.locks.has(slot)) this.locks.delete(slot); else this.locks.set(slot,choice);
      this.updateLock(button,choice);this.updateControls();
      this.querySelector('[data-czu-status]').textContent=productName(choice)+': '+this.label(this.locks.has(slot)?'locked':'unlocked');
    },{signal:this.abort.signal});
    this.querySelector('[data-czu-shuffle]').addEventListener('click',() => {this.round++;this.renderOutfit(true);},{signal:this.abort.signal});
    this.querySelector('[data-czu-retry]').addEventListener('click',() => this.loadUniform(true),{signal:this.abort.signal});
    if('IntersectionObserver' in window) {
      this.observer=new IntersectionObserver(entries => { if(entries.some(entry=>entry.isIntersecting)){this.observer.disconnect();this.loadUniform();} },{rootMargin:'600px'});
      this.observer.observe(this);
    } else this.loadUniform();
  }
  async loadUniform(force=false) {
    const generation=++this.generation;
    const slots=outfitSlots(this.product);
    const required=slots.flatMap(slot=>slot==='layer'?['outer','mid']:[slot]);
    const urls=[...new Set(required.map(key=>this.pools[key]).filter(Boolean))];
    if(!urls.length) {this.empty();return;}
    this.querySelector('[data-czu-status]').textContent=this.label('loading');
    this.querySelector('[data-czu-retry]').hidden=true;
    this.querySelector('[data-czu-outfit]').setAttribute('aria-busy','true');
    try {
      const results=await Promise.allSettled(urls.map(async url => {
        let products=[];
        const roles=required.filter(key=>this.pools[key]===url);
        for(let page=1;page<=3;page++) {
          let result;
          try { result=await getFeed(url,page,force); }
          catch(error) { if(!products.length) throw error; break; }
          products=products.concat(result.products || []);
          const useful=products.map(item=>scoreCandidate(this.product,item,productColor(this.product))).filter(Boolean);
          if(!result.next || roles.every(role=>useful.filter(item=>item.role===role).length>=8)) break;
        }
        return products;
      }));
      if(!this.isConnected || generation!==this.generation) return;
      if(results.every(result=>result.status==='rejected')) throw new Error('No feeds available');
      const products=results.filter(result=>result.status==='fulfilled').flatMap(result=>result.value);
      this.candidates=[...new Map(products.map(item=>[String(item.id),{...item,analysis:analyse(item)}])).values()];
      this.partialFailure=results.some(result=>result.status==='rejected');
      this.renderOutfit(false);
    } catch {
      if(!this.isConnected || generation!==this.generation) return;
      this.querySelectorAll('[data-czu-placeholder]').forEach(node=>node.remove());
      this.querySelector('[data-czu-status]').textContent=this.label('error');
      this.querySelector('[data-czu-retry]').hidden=false;
    } finally { if(this.isConnected && generation===this.generation) this.querySelector('[data-czu-outfit]').setAttribute('aria-busy','false'); }
  }
  empty() {
    if(window.Shopify?.designMode) {this.querySelector('[data-czu-status]').textContent=this.label('editor_empty');this.querySelectorAll('[data-czu-placeholder]').forEach(node=>node.remove());}
    else this.hidden=true;
  }
  renderOutfit(announce,target=null) {
    const previous=this.choices;
    const active=document.activeElement;
    const focusedSlot=this.contains(active) ? active?.dataset.czuChange : null;
    const choices=chooseOutfit(this.product,this.candidates,this.round,{locks:this.locks,previous:announce?previous:[],target});
    if(!choices.length) { if(this.partialFailure){this.querySelector('[data-czu-status]').textContent=this.label('error');this.querySelector('[data-czu-retry]').hidden=false;} else this.empty(); return; }
    this.hidden=false;this.choices=choices;
    const grid=this.querySelector('[data-czu-outfit]');
    grid.querySelectorAll('[data-czu-placeholder]').forEach(node=>node.remove());
    grid.style.setProperty('--czu-card-count',String(choices.length+1));
    grid.dataset.czuCount=String(choices.length+1);
    const existing=new Map([...grid.querySelectorAll('[data-czu-recommendation]')].map(node=>[node.dataset.czuSlot,node]));
    for(const choice of choices) {
      const prior=existing.get(choice.slot);
      if(prior && prior.dataset.czuKey===choiceKey(choice)) { existing.delete(choice.slot);continue; }
      const card=this.createCard(choice);
      if(prior) { prior.replaceWith(card);existing.delete(choice.slot); } else grid.append(card);
    }
    existing.forEach(node=>node.remove());
    // Keep stable slot order when a previously empty slot becomes available.
    let after=grid.querySelector('.cz-uniform__card--current');
    for(const choice of choices) {
      const card=[...grid.querySelectorAll('[data-czu-recommendation]')].find(node=>node.dataset.czuSlot===choice.slot);
      if(after?.nextElementSibling!==card) after?.after(card);
      after=card;
    }
    this.updateControls();
    if(focusedSlot) {
      const card=[...grid.querySelectorAll('[data-czu-recommendation]')].find(node=>node.dataset.czuSlot===focusedSlot);
      const control=card?.querySelector('[data-czu-change]');
      (control && !control.disabled ? control : card?.querySelector('h3 a'))?.focus({preventScroll:true});
    }
    const changed=choices.some(choice=>choiceKey(choice)!==choiceKey(previous.find(item=>item.slot===choice.slot)));
    const current=choices.find(choice=>choice.slot===target);
    this.querySelector('[data-czu-status]').textContent=announce?(target?(changed?this.label('piece_updated')+' '+productName(current):this.label('no_alternative')):this.label('updated')):(choices.length===1?this.label('one_pair'):this.label('many_pairs'));
  }
  updateControls() {
    const next=chooseOutfit(this.product,this.candidates,this.round+1,{locks:this.locks,previous:this.choices});
    const canChange=next.some(item=>choiceKey(item)!==choiceKey(this.choices.find(prior=>prior.slot===item.slot)));
    this.querySelector('[data-czu-shuffle]').disabled=!canChange;
    for(const button of this.querySelectorAll('[data-czu-change]')) {
      const slot=button.dataset.czuChange, choice=this.choices.find(item=>item.slot===slot);
      if(!choice) continue;
      const locked=this.locks.has(slot);
      const nextPiece=chooseOutfit(this.product,this.candidates,this.round+1,{locks:this.locks,previous:this.choices,target:slot}).find(item=>item.slot===slot);
      const available=!locked && Boolean(nextPiece) && choiceKey(nextPiece)!==choiceKey(choice);
      button.disabled=!available;
      button.querySelector('span').textContent=this.label(locked?'locked':'change_piece');
      const action=this.label(locked?'locked':available?'change_piece':'no_alternative');
      button.setAttribute('aria-label',action+': '+productName(choice));
      button.title=action;
    }
    const hint=this.querySelector('[data-czu-lock-hint]');
    if(hint) hint.textContent=this.label(this.choices.length && this.choices.every(item=>this.locks.has(item.slot))?'all_locked':canChange?'lock_hint':'no_alternatives');
  }
  updateLock(button,choice) {
    const locked=this.locks.has(choice.slot);
    button.setAttribute('aria-pressed',String(locked));
    button.setAttribute('aria-label',this.label(locked?'unlock_item':'lock_item')+': '+productName(choice));
    button.title=this.label(locked?'unlock_item':'lock_item');
    button.querySelector('span').textContent=this.label('locked');
    button.closest('[data-czu-recommendation]')?.toggleAttribute('data-locked',locked);
  }
  createCard(choice) {
    const {product,color,role,slot}=choice;
    const card=document.createElement('article');card.className='cz-uniform__card';card.dataset.czuRecommendation='';card.dataset.czuSlot=slot;card.dataset.czuKey=choiceKey(choice);
    const roleNode=document.createElement('p');roleNode.className='cz-uniform__role';roleNode.textContent=this.label(role);
    const url=new URL(safeURL(product.url,true));url.searchParams.set('variant',String(color.id));
    const media=document.createElement('div');media.className='cz-uniform__card-media';
    const photo=document.createElement('a');photo.className='cz-uniform__card-photo';photo.href=url.href;photo.tabIndex=-1;photo.setAttribute('aria-hidden','true');
    photo.append(imageElement(color.image || product.image,'','cz-uniform__image'));
    const lock=document.createElement('button');lock.className='cz-uniform__lock';lock.type='button';lock.dataset.czuLock=slot;
    lock.innerHTML='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path class="cz-uniform__lock-open" d="M8 10V6a4 4 0 0 1 7.8-1.2"/><path class="cz-uniform__lock-closed" d="M8 10V6a4 4 0 0 1 8 0v4"/><path d="M12 14v3"/></svg><span></span>';
    media.append(photo,lock);
    const copy=document.createElement('div');copy.className='cz-uniform__card-copy';
    const vendor=document.createElement('p');vendor.className='cz-uniform__vendor';vendor.textContent=clean(product.vendor);vendor.hidden=!vendor.textContent;
    const title=document.createElement('h3');const link=document.createElement('a');link.href=url.href;link.textContent=product.title;title.append(link);
    title.id=(this.getAttribute('aria-labelledby') || 'czu-'+this.product.id)+'-'+slot+'-title';
    const colorNode=document.createElement('p');colorNode.className='cz-uniform__card-color';colorNode.textContent=color.color || '';
    const price=document.createElement('p');price.className='cz-uniform__price';price.textContent=color.price;
    const meta=document.createElement('div');meta.className='cz-uniform__card-meta';meta.append(colorNode,price);colorNode.hidden=!color.color;
    const actions=document.createElement('div');actions.className='cz-uniform__card-actions';
    const change=document.createElement('button');change.className='cz-uniform__change';change.type='button';change.dataset.czuChange=slot;
    change.innerHTML='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 7h-4m4 0V3m0 4A9 9 0 0 0 5 6M4 17h4m-4 0v4m0-4a9 9 0 0 0 15 1"/></svg><span></span>';
    const wishlist=document.createElement('cz-wishlist-toggle');wishlist.className='cz-wl-toggle cz-uniform__wishlist';wishlist.dataset.czWishlistContext='uniform';wishlist.hidden=true;
    const save=document.createElement('button');save.className='cz-wl-button';save.type='button';save.dataset.czWishlistToggle='';save.dataset.iconOnly='';save.disabled=true;
    Object.assign(save.dataset,{productId:String(product.id),productHandle:product.handle,productTitle:product.title,productVendor:product.vendor || '',productImage:color.image || product.image});
    save.setAttribute('aria-pressed','false');save.setAttribute('aria-describedby',title.id);
    save.innerHTML='<svg class="cz-wl-heart" aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg><span data-cz-wishlist-label></span>';
    const buy=document.createElement('a');buy.className='cz-uniform__buy';buy.href=url.href;buy.textContent=this.label('add_to_bag');buy.dataset.czQuickBuy='';buy.dataset.productId=String(product.id);buy.setAttribute('aria-haspopup','dialog');buy.setAttribute('aria-label',this.label('add_to_bag')+': '+product.title);
    wishlist.append(save);actions.classList.add('cz-uniform__card-actions--purchase');actions.append(buy,wishlist,change);copy.append(vendor,title,meta,actions);
    card.append(roleNode,media,copy);this.updateLock(lock,choice);return card;
  }
}
if(!customElements.get('cz-uniform')) customElements.define('cz-uniform',CZUniform);
let lastVariantEvent=0;
document.addEventListener('variant:update',event => {
  if(event.target?.closest?.('product-card,quick-add-dialog,quick-add-component')) return;
  const detail=event.detail || {}, id=detail.data?.productId || detail.productId;
  if(!id && !event.target?.closest?.('variant-picker,variant-selects,variant-radios')) return;
  lastVariantEvent=Date.now();
  for(const instance of instances) {
    if(id && String(id)!==String(instance.product.id)) continue;
    instance.setVariant(detail.resource || detail.variant);
  }
});
let syncTimer;
function scheduleVariantSync() {
  clearTimeout(syncTimer);
  syncTimer=setTimeout(async () => {
    for(const instance of instances) {
      const id=new URL(window.location.href).searchParams.get('variant');
      if(!id || id===String(instance.product.selected?.id)) continue;
      try {
        const url=new URL(safeURL(instance.product.url,true));url.searchParams.set('variant',id);
        const data=await getFeed(url.href);
        if(new URL(window.location.href).searchParams.get('variant')!==id) continue;
        for(const peer of instances) if(String(peer.product.id)===String(data.id)) peer.setVariant(data.selected);
      } catch {}
      break;
    }
  },400);
}
window.addEventListener('popstate',scheduleVariantSync);
document.addEventListener('change',event => {
  if(event.target?.closest?.('variant-picker,variant-selects,variant-radios') && !event.target.closest('product-card,quick-add-dialog')) {
    const started=Date.now();setTimeout(()=>{if(lastVariantEvent<started)scheduleVariantSync();},600);
  }
});
export { analyse, classify, chooseOutfit, colorFamily, colorScore, descriptionLines, scoreCandidate, outfitSlots };


